import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Engine, JobFilters, SourceKind } from "../lib/types";

interface JobDraftState {
  engine: Engine;
  mode: SourceKind;
  code: string;
  filename: string;
  optimize: boolean;
  autoDownload: boolean;
  wrapEditor: boolean;
  filters: JobFilters;
  realtimeState: "connecting" | "connected" | "fallback";
  setEngine: (engine: Engine) => void;
  setMode: (mode: SourceKind) => void;
  setCode: (code: string) => void;
  setFilename: (filename: string) => void;
  setOptimize: (optimize: boolean) => void;
  setAutoDownload: (autoDownload: boolean) => void;
  setWrapEditor: (wrapEditor: boolean) => void;
  setFilters: (filters: JobFilters) => void;
  setRealtimeState: (state: JobDraftState["realtimeState"]) => void;
  loadExample: (engine: Exclude<Engine, "auto">) => void;
  resetDraft: () => void;
}

const examples: Record<Exclude<Engine, "auto">, { filename: string; code: string }> = {
  graphviz: {
    filename: "binary-tree.dot",
    code: `digraph BinaryTree {
  graph [bgcolor="transparent", ranksep=0.55, nodesep=0.4];
  node [shape=circle, style=filled, fillcolor="#f5f7ff", color="#6870d8"];
  A -> {B C};
  B -> {D E};
  C -> {F G};
}`,
  },
  d2: {
    filename: "architecture.d2",
    code: `direction: right
browser: Browser
api: API
storage: Storage
browser -> api: request
api -> storage: read/write`,
  },
  tikz: {
    filename: "vector-plane.tex",
    code: `\\documentclass[tikz,border=8pt]{standalone}
\\usetikzlibrary{arrows.meta}
\\begin{document}
\\begin{tikzpicture}[>=Stealth]
  \\draw[->,thick] (0,0) -- (3,0.6) node[right] {$\\mathbf a_1$};
  \\draw[->,thick] (0,0) -- (1.1,2.2) node[above] {$\\mathbf a_2$};
  \\draw[dashed] (3,0.6) -- (4.1,2.8) -- (1.1,2.2);
\\end{tikzpicture}
\\end{document}`,
  },
  pgfplots: {
    filename: "function-plot.tex",
    code: `\\documentclass[border=8pt]{standalone}
\\usepackage{pgfplots}
\\pgfplotsset{compat=1.18}
\\begin{document}
\\begin{tikzpicture}
  \\begin{axis}[axis lines=middle,grid=both]
    \\addplot[domain=-3:3,samples=120,thick] {x^2};
  \\end{axis}
\\end{tikzpicture}
\\end{document}`,
  },
};

const initialDraft = {
  engine: "auto" as Engine,
  mode: "code" as SourceKind,
  code: examples.graphviz.code,
  filename: examples.graphviz.filename,
  optimize: true,
  autoDownload: false,
  wrapEditor: false,
  filters: { status: "all", search: "" } as JobFilters,
  realtimeState: "connecting" as const,
};

export const useJobStore = create<JobDraftState>()(
  persist(
    (set) => ({
      ...initialDraft,
      setEngine: (engine) => set({ engine }),
      setMode: (mode) => set({ mode }),
      setCode: (code) => set({ code }),
      setFilename: (filename) => set({ filename }),
      setOptimize: (optimize) => set({ optimize }),
      setAutoDownload: (autoDownload) => set({ autoDownload }),
      setWrapEditor: (wrapEditor) => set({ wrapEditor }),
      setFilters: (filters) => set({ filters }),
      setRealtimeState: (realtimeState) => set({ realtimeState }),
      loadExample: (engine) => set({ engine, mode: "code", ...examples[engine] }),
      resetDraft: () => set((state) => ({
        engine: state.engine,
        mode: "code",
        code: examples.graphviz.code,
        filename: examples.graphviz.filename,
      })),
    }),
    {
      name: "svg-render-workspace-v2",
      partialize: (state) => ({
        engine: state.engine,
        mode: state.mode,
        code: state.code,
        filename: state.filename,
        optimize: state.optimize,
        autoDownload: state.autoDownload,
        wrapEditor: state.wrapEditor,
        filters: state.filters,
      }),
    },
  ),
);
